import { Button } from "@/atoms-react/button/Button.react";
import { Modal, ModalContent, type ModalProps } from "@/atoms-react/modal/index.react";
import { useUser } from "@/features/user/hooks/use-user";
import {
  isAddressCreateEqualToAddress,
  type AddressCreate,
  type AddressFullType
} from "@/features/user/types";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { FormAddAddress } from "@/molecules/form-add-address/index.react";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type ModalAddAddressProps = {
  address?: AddressFullType | null;
  onValidated?: (address: AddressCreate) => void;
} & ModalProps;

const getDefaultAddressData = () => ({
  address: "",
  zip: "",
  country: null,
  city: "",
  tag: "",
  isPrincipalAddress: false
});

export const ModalAddAddress = ({
  address,
  onValidated = async () => {},
  ...props
}: ModalAddAddressProps) => {
  const isAddNewAddress = address === null || address === undefined;
  const { t } = useTranslation(["atoms"]);
  const { useUserProfile } = useUser();
  const { data: user, isLoading: isLoadingUser } = useUserProfile();
  const { locale } = useLocale();
  const isFrench = locale.language === "FR";
  const [createdAddress, setCreatedAddress] = useState<AddressCreate>({
    address: address?.Address1 ?? "",
    zip: address?.ZipCode ?? "",
    country: parseInt(address?.Country as string) ?? null,
    city: address?.City ?? "",
    tag: address?.Tag || "",
    isPrincipalAddress: address?.isPrincipalAddress || false
  });

  useEffect(() => {
    if (!address) {
      setCreatedAddress(getDefaultAddressData());
    } else {
      setCreatedAddress({
        address: address.Address1,
        zip: address.ZipCode,
        country: parseInt(address.Country),
        city: address.City,
        tag: address.Tag || "",
        isPrincipalAddress: address.isPrincipalAddress || false
      });
    }
  }, [address]);

  useEffect(() => {
    if (isLoadingUser) return;

    setCreatedAddress((data) => ({
      ...data,
      UUID: user?.naturalPerson?.UUID,
      language: isFrench ? "fr" : "en"
    }));
  }, [user]);

  const [addressValid, setAddressValid] = useState(false);
  const isValid = (): boolean =>
    !!createdAddress.address &&
    !!createdAddress.country &&
    !!createdAddress.city &&
    !!createdAddress.tag &&
    (isAddNewAddress ? true : !isAddressCreateEqualToAddress(createdAddress, address)) &&
    addressValid;
  return (
    <Modal {...props} maxWidthClassName="max-w-2xl py-6">
      <div className="px-2 py-5">
        <ModalContent
          title={
            isAddNewAddress
              ? t("molecules.modal_add_address.title")
              : t("molecules.modal_add_address.title_modify")
          }
        >
          <div className={"mt-4 flex flex-col items-end opacity-1"}>
            <div className={"mt-4 mb-16 w-full"}>
              <FormAddAddress
                autoFocus
                residence={true}
                address={createdAddress}
                onChange={(address, valid) => {
                  setAddressValid(valid);
                  setCreatedAddress(address);
                }}
                isPrincipalAddress={address?.isPrincipalAddress}
              />
            </div>

            <Button
              disabled={!isValid()}
              shortcut="enter"
              className="w-max shrink-0"
              onClick={async () => await onValidated(createdAddress)}
              icon={<ChevronRightIcon />}
              dataCy={"button-save-address"}
            >
              {t("molecules.modal_add_address.confirm")}
            </Button>
          </div>
        </ModalContent>
      </div>
    </Modal>
  );
};
